import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { ApiClientError } from "./lib/api";
import { ThemeProvider } from "./lib/shell";
import { App } from "./App";
import "./styles/industry.css";
import "./styles/industry-v2.css";
import "./styles/responsive.css";
// After industry-v2.css: Settings -> Appearance and Accessibility Mode.
import "./styles/accessibility.css";
import "./components/AssetWorkOrderKanban.css";
import "./components/AssetFileLibrary.css";
import "./components/AssignedSbDrivers.css";
import "./screens/settings/SbDriverFeed.css";
import "./styles/site-maintenance.css";
import "./screens/TimeClock.css";
import "./screens/AssetRecord.css";
import "./components/AppNavigation.css";
import "./components/NotificationBell.css";
// TRBUD-439: one stylesheet per renovation work package, after every global sheet.
import "./styles/queue-touch.css";
import "./styles/overview.css";
import "./styles/reports-pages.css";
// TRBUD-448: Parts, Vendors and Reports fixes from the 23 October review.
import "./styles/screens-448.css";
import "./styles/purchase-orders.css";
import "./styles/work-order.css";
import "./styles/work-order-head.css";
import "./styles/work-order-band.css";
import "./styles/work-order-documents.css";
import "./styles/work-order-timeline.css";
import "./styles/work-order-ledgers.css";
import "./styles/work-order-details.css";
import "./styles/work-order-packages.css";
// TRBUD-466: the vendor data completeness page and the missing-field banner.
import "./screens/vendors/vendor-completeness.css";
// TRBUD-466 Part 2: the spending insights panel on a vendor's page.
import "./screens/vendors/vendor-insights.css";


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30 seconds: operational screens must not show a stale queue, but
      // remounting a tab should not refetch everything either.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry a refusal. A 403 will refuse again, a 404 will still be
        // missing, and a 409 means the world moved — retrying any of them just
        // delays the message the user needs. Transient failures get two tries.
        if (error instanceof ApiClientError && error.status >= 400 && error.status < 500) {
          return false;
        }
        // Nor a maintenance window: it answers 503 to every retry until it
        // ends, and the shell already shows the maintenance page and refetches
        // everything once it does.
        if (error instanceof ApiClientError && error.isSiteMaintenance) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      // Mutations opt in individually. A blanket retry on a write is how a
      // double-post happens on an endpoint whose idempotency key is only
      // in-process (B7).
      retry: false,
    },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("#root is missing from index.html");

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
